// components/EscalationDetailPage.js
// Case detail panel for /escalations-claims/:id — three columns:
// left: case & order info, center: follow-up history timeline, right: actions.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Divider, Spin, Tag } from "antd";
import {
  ArrowLeftOutlined, CheckCircleOutlined, EditOutlined, HistoryOutlined, PlusOutlined,
  StopOutlined, SyncOutlined, UserOutlined,
} from "@ant-design/icons";
import Cookies from "js-cookie";
import config from "../config.js";
import { errMsg, fetchCase, fetchOrderHistory } from "../services/escalationService";
import {
  EscalationFormDrawer, FollowUpModal, OrderHistoryModal, RejectModal, ResolveModal,
  fmtDate, fmtDateTime, statusTag,
} from "./EscalationModals";
import "./EscalationsClaims.css";

const InfoRow = ({ label, value, mono = false }) => (
  <div className="ec-info-row">
    <span>{label}</span>
    <b className={mono ? "ec-mono" : ""}>{value === undefined || value === null || value === "" ? "—" : value}</b>
  </div>
);

const EscalationDetailPage = ({ darkTheme = false, isMobile = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = Cookies.get("token") || undefined;

  const [record, setRecord] = useState(null);
  const [relatedCases, setRelatedCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rec = await fetchCase(token, id);
      setRecord(rec);
      try {
        const hist = await fetchOrderHistory(token, rec.salesOrderId);
        setRelatedCases((hist.cases || []).filter((c) => c._id !== rec._id));
      } catch (err) {
        setRelatedCases([]); // related history is supplementary — never blocks the detail view
      }
    } catch (err) {
      setError(errMsg(err, "Failed to load escalation"));
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="ec-detail-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="ec-container">
        <Alert
          type="error"
          showIcon
          message={error || "Escalation not found"}
          action={<Button onClick={() => navigate("/escalations-claims")}>Back to Register</Button>}
        />
      </div>
    );
  }

  const history = [...(record.followUpHistory || [])].sort(
    (a, b) => new Date(b.followUpDate) - new Date(a.followUpDate) || new Date(b.performedAt) - new Date(a.performedAt)
  );
  const audit = [...(record.auditTrail || [])].sort((a, b) => new Date(b.at) - new Date(a.at));
  const isOpen = record.isOpen;

  return (
    <div className={`ec-container ${darkTheme ? "ec-dark" : ""}`}>
      <div className="ec-head ec-detail-head">
        <div className="ec-head-left ec-detail-id">
          <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate("/escalations-claims")} aria-label="Back to register" />
          <h1 className="ec-title">{record.escalationId}</h1>
          {statusTag(record.status)}
          {record.overdue && <Tag color="red" className="ec-overdue-tag">Overdue +{record.daysOverdue}d</Tag>}
          <span className="ec-detail-meta">{record.actionType} · {record.issueReason} · Order {record.salesOrderId}</span>
        </div>
        <div className="ec-head-actions ec-detail-actions">
          {isOpen && <Button size="small" icon={<EditOutlined />} onClick={() => setDrawerOpen(true)}>Edit</Button>}
          {isOpen && <Button size="small" icon={<PlusOutlined />} type="primary" onClick={() => setFollowUpOpen(true)}>Add Follow-up</Button>}
        </div>
      </div>

      <div className={`ec-detail-grid ${isMobile ? "ec-detail-grid-mobile" : ""}`}>
        {/* ── Left: case & order information ── */}
        <section className="ec-panel">
          <h3 className="ec-panel-title"><UserOutlined /> Case Information</h3>
          <div className="ec-status-strip">
            {statusTag(record.status)}
            {record.overdue && <Tag color="red" style={{ marginLeft: 8 }}>Overdue +{record.daysOverdue}d</Tag>}
          </div>
          <InfoRow label="Sales Order ID" value={record.salesOrderId} mono />
          <InfoRow label="AWB / Tracking Code" value={record.awbCode} mono />
          <InfoRow label="Carrier" value={record.carrierDisplay} />
          <InfoRow label="Issue / Reason" value={record.issueReason} />
          <InfoRow label="Action Type" value={record.actionType} />
          <InfoRow label="Escalated Via" value={record.escalatedVia} />
          <InfoRow label="Escalation Date" value={fmtDate(record.escalationDate)} />
          <InfoRow label="Assigned To" value={record.assignedTo} />
          <InfoRow label="Last Follow-up" value={fmtDate(record.lastFollowUpDate)} />
          <InfoRow label="Next Follow-up" value={isOpen ? fmtDate(record.nextFollowUpDate) : "—"} />
          <InfoRow label="Latest Remarks" value={record.latestRemarks} />
          <Divider orientation="left" plain>Customer (from order data)</Divider>
          <InfoRow label="Name" value={record.customerSnapshot?.name} />
          <InfoRow label="Email" value={record.customerSnapshot?.email} />
          <InfoRow label="Mobile" value={record.customerSnapshot?.mobile} />
          {record.status === "Resolved" && (
            <>
              <Divider orientation="left" plain>Resolution</Divider>
              <InfoRow label="Resolved On" value={fmtDate(record.resolutionDate)} />
              <InfoRow label="Resolution Remarks" value={record.resolutionRemarks} />
            </>
          )}
          {record.status === "Rejected" && (
            <>
              <Divider orientation="left" plain>Rejection</Divider>
              <InfoRow label="Rejected On" value={fmtDate(record.closedDate)} />
              <InfoRow label="Rejection Reason" value={record.rejectionReason} />
            </>
          )}
          {record.attachments && record.attachments.length > 0 && (
            <>
              <Divider orientation="left" plain>Attachments</Divider>
              <div className="ec-attach-list">
                {record.attachments.map((a, i) => (
                  <a key={i} href={`${config.apiBaseUrl}${a.url}`} className="ec-attach" target="_blank" rel="noreferrer">
                    {a.name}
                  </a>
                ))}
              </div>
            </>
          )}
          <Divider orientation="left" plain>Audit</Divider>
          <InfoRow label="Created By" value={`${record.createdBy || "—"} · ${fmtDateTime(record.createdAt)}`} />
          <InfoRow label="Last Updated By" value={`${record.updatedBy || "—"} · ${fmtDateTime(record.updatedAt)}`} />
        </section>

        {/* ── Center: follow-up history timeline ── */}
        <section className="ec-panel ec-panel-center">
          <h3 className="ec-panel-title"><SyncOutlined /> Follow-up History ({history.length})</h3>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="History is append-only — entries are never overwritten or deleted."
          />
          {history.length === 0 && (
            <div className="ec-timeline-empty">No follow-ups recorded yet.</div>
          )}
          <div className="ec-timeline">
            {history.map((h, idx) => (
              <div key={idx} className="ec-timeline-item">
                <div className="ec-timeline-dot" />
                <div className="ec-timeline-body">
                  <div className="ec-timeline-head">
                    <b>{fmtDate(h.followUpDate)}</b>
                    <Tag style={{ background: "#ecfdf5", color: "#0b5d3b", border: "none", borderRadius: 6 }}>{h.channel}</Tag>
                    {h.statusAfter && statusTag(h.statusAfter)}
                  </div>
                  {h.contactedParty && <div className="ec-timeline-line"><span>Party:</span> {h.contactedParty}</div>}
                  <div className="ec-timeline-line"><span>Outcome:</span> {h.outcome}</div>
                  <div className="ec-timeline-line"><span>Remarks:</span> {h.remarks}</div>
                  <div className="ec-timeline-meta">
                    Next follow-up: {fmtDate(h.nextFollowUpDate)} · by <b>{h.performedBy || "—"}</b> {h.performedByEmail ? `(${h.performedByEmail})` : ""}
                  </div>
                  {h.attachments && h.attachments.length > 0 && (
                    <div className="ec-attach-list">
                      {h.attachments.map((a, i) => (
                        <a key={i} href={`${config.apiBaseUrl}${a.url}`} className="ec-attach" target="_blank" rel="noreferrer">{a.name}</a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <Divider orientation="left" plain>Audit Trail</Divider>
          <div className="ec-audit-list">
            {audit.map((a, i) => (
              <div key={i} className="ec-audit-item">
                <b>{a.action}</b> — {a.details || "—"}<br />
                <span>{fmtDateTime(a.at)} · {a.byName}{a.byEmail ? ` (${a.byEmail})` : ""}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Right: actions & workflow ── */}
        <section className="ec-panel">
          <h3 className="ec-panel-title">Actions &amp; Workflow</h3>
          <div className="ec-action-list">
            <Button block type="primary" icon={<PlusOutlined />} disabled={!isOpen} onClick={() => setFollowUpOpen(true)}>
              Add Follow-up
            </Button>
            <Button block icon={<EditOutlined />} disabled={!isOpen} onClick={() => setDrawerOpen(true)}>
              Edit Case
            </Button>
            <Button block icon={<CheckCircleOutlined />} disabled={!isOpen} onClick={() => setResolveOpen(true)}>
              Mark Resolved
            </Button>
            <Button block danger icon={<StopOutlined />} disabled={!isOpen} onClick={() => setRejectOpen(true)}>
              Mark Rejected
            </Button>
            <Button block icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
              Order Escalation History
            </Button>
          </div>

          <Divider orientation="left" plain>Workflow Rules</Divider>
          <ul className="ec-rules">
            <li>Statuses: Need to Follow Up → Follow-up Completed → Waiting for Response → Resolved / Rejected.</li>
            <li>Overdue is a calculated flag — it never changes the status.</li>
            <li>Completing a follow-up keeps the case open; only Resolve / Reject closes it.</li>
            <li>Resolved requires remarks + date; Rejected requires a reason + closure date.</li>
            <li>Unresolved cases must keep an assignee and a next follow-up date.</li>
          </ul>

          {relatedCases.length > 0 && (
            <>
              <Divider orientation="left" plain>Other cases for this order</Divider>
              <div className="ec-related">
                {relatedCases.map((c) => (
                  <Button
                    key={c._id}
                    size="small"
                    block
                    style={{ marginBottom: 6, textAlign: "left" }}
                    onClick={() => navigate(`/escalations-claims/${c._id}`)}
                  >
                    {c.escalationId} · {c.status}
                  </Button>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      {/* Dialogs */}
      <EscalationFormDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} record={record} onSaved={load} isMobile={isMobile} />
      <FollowUpModal open={followUpOpen} record={record} onClose={() => setFollowUpOpen(false)} onSaved={load} />
      <ResolveModal open={resolveOpen} record={record} onClose={() => setResolveOpen(false)} onSaved={load} />
      <RejectModal open={rejectOpen} record={record} onClose={() => setRejectOpen(false)} onSaved={load} />
      <OrderHistoryModal open={historyOpen} salesOrderId={record.salesOrderId} onClose={() => setHistoryOpen(false)} darkTheme={darkTheme} />
    </div>
  );
};

export default EscalationDetailPage;


